import { Search, X } from 'lucide-react';
import type { ListedAutomation } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Input, Select } from '../ui/input';

/**
 * Narrowing the org-wide automation list to the ones a person cares about.
 *
 * The list is every automation in the organization, which is the right *set* — an automation reads
 * across the hierarchy and is managed centrally — and the wrong *default view* once there are dozens:
 * the two questions people actually arrive with are "what have I set up" and "what lands in my Slack".
 * So the first control answers those, and the rest narrow whichever is showing.
 *
 * "Sent to me" is a Slack DM to the viewer, worked out on the server (Slack's lookup by email — see
 * `AutomationsService.slackUserIdFor`). A channel the viewer is in is not counted: that is somewhere
 * they could read it, not something delivered to them.
 */

export type ShowFilter = 'mine' | 'created' | 'toMe' | 'all';
export type DeliveryFilter = 'any' | 'slack' | 'drive' | 'none';

export interface AutomationFilterState {
  show: ShowFilter;
  q: string;
  delivery: DeliveryFilter;
}

export const SHOW_OPTIONS: Array<{ key: ShowFilter; label: string; title: string }> = [
  { key: 'mine', label: 'Mine', title: 'Automations you created, and ones delivered to you in Slack' },
  { key: 'created', label: 'Created by me', title: 'Automations you created' },
  { key: 'toMe', label: 'Sent to me', title: 'Automations that send their document to you as a Slack DM' },
  { key: 'all', label: 'All', title: 'Every automation in the organization' },
];

const DELIVERIES: Array<{ key: DeliveryFilter; label: string }> = [
  { key: 'any', label: 'Any delivery' },
  { key: 'slack', label: 'Posts to Slack' },
  { key: 'drive', label: 'Files to Drive' },
  { key: 'none', label: 'Console only' },
];

export const DEFAULT_FILTERS: AutomationFilterState = {
  show: 'mine',
  q: '',
  delivery: 'any',
};

/** URL keys, so a filtered view is a link worth sharing and survives a reload. */
const PARAM: Record<keyof AutomationFilterState, string> = {
  show: 'show',
  q: 'q',
  delivery: 'delivery',
};

function oneOf<T extends string>(value: string | null, options: Array<{ key: T }>, fallback: T): T {
  return options.some((o) => o.key === value) ? (value as T) : fallback;
}

export function filtersFromParams(params: URLSearchParams, show: ShowFilter): AutomationFilterState {
  return {
    show: oneOf(params.get(PARAM.show), SHOW_OPTIONS, show),
    q: params.get(PARAM.q) ?? '',
    delivery: oneOf(params.get(PARAM.delivery), DELIVERIES, 'any'),
  };
}

/**
 * Writes the filters onto `params`, leaving out anything at its default so the URL stays short —
 * except `show`, whose default depends on whether the viewer has any automations of their own, so a
 * choice that happens to equal today's default must still be written down.
 */
export function writeFilterParams(params: URLSearchParams, filters: AutomationFilterState): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.keys(PARAM) as Array<keyof AutomationFilterState>) {
    const value = filters[key];
    if (key !== 'show' && (value === '' || value === DEFAULT_FILTERS[key])) next.delete(PARAM[key]);
    else next.set(PARAM[key], value);
  }
  return next;
}

export function matchesShow(a: ListedAutomation, show: ShowFilter): boolean {
  if (show === 'created') return a.createdByMe;
  if (show === 'toMe') return a.deliveredToMe;
  if (show === 'mine') return a.createdByMe || a.deliveredToMe;
  return true;
}

/** Everything but `show`, which the tabs count separately so each says what it would hold. */
export function matchesRest(a: ListedAutomation, f: AutomationFilterState): boolean {
  if (f.delivery === 'slack' && !a.postToSlack) return false;
  if (f.delivery === 'drive' && !a.saveToDrive) return false;
  if (f.delivery === 'none' && (a.postToSlack || a.saveToDrive)) return false;
  const q = f.q.trim().toLowerCase();
  if (q) {
    const haystack = [a.name, a.description, a.createdByName, a.projectName].filter(Boolean).join(' ').toLowerCase();
    if (!q.split(/\s+/).every((term) => haystack.includes(term))) return false;
  }
  return true;
}

export function AutomationFilterBar({
  filters,
  onChange,
  automations,
}: {
  filters: AutomationFilterState;
  onChange: (next: AutomationFilterState) => void;
  automations: ListedAutomation[];
}) {
  const set = <K extends keyof AutomationFilterState>(key: K, value: AutomationFilterState[K]) =>
    onChange({ ...filters, [key]: value });

  // Counted against the other filters, so a tab's number is what clicking it would show.
  const rest = automations.filter((a) => matchesRest(a, filters));
  const count = (show: ShowFilter) => rest.filter((a) => matchesShow(a, show)).length;

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Whose automations" className="inline-flex rounded-md border border-border bg-surface-muted p-0.5">
          {SHOW_OPTIONS.map(({ key, label, title }) => {
            const active = filters.show === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                title={title}
                onClick={() => set('show', key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  active ? 'bg-card text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
                )}
              >
                {label}
                <span className={cn('tabular-nums text-[10px]', active ? 'text-ink-muted' : 'text-ink-faint')}>{count(key)}</span>
              </button>
            );
          })}
        </div>

        <Select
          value={filters.delivery}
          onChange={(e) => set('delivery', e.target.value as DeliveryFilter)}
          aria-label="Delivery"
          className="ml-auto w-auto py-1.5 text-xs"
        >
          {DELIVERIES.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </Select>
        <div className="relative w-full sm:w-64">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-2 text-ink-faint" />
          <Input
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
            placeholder="Search name, description, creator…"
            aria-label="Search automations"
            className="py-1.5 pl-8 pr-7 text-xs"
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => set('q', '')}
              aria-label="Clear the search"
              className="absolute right-2 top-2 text-ink-faint transition-colors hover:text-ink"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
